import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { tvShows } from '@/mocks/tvShows';
import type { TvShow } from '@/mocks/tvShows';

export default function HeroBanner() {
  const featuredShows = tvShows.filter(s => s.featured);
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  const next = useCallback(() => {
    goTo((current + 1) % featuredShows.length);
  }, [current, featuredShows.length, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const show: TvShow = featuredShows[current];

  if (featuredShows.length === 0) return null;

  return (
    <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={show.poster}
          alt={show.name}
          className={`w-full h-full object-cover object-top transition-opacity duration-600 ${isTransitioning ? 'opacity-40' : 'opacity-100'}`}
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/70 to-transparent"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent"></div>

      <div className="relative z-10 h-full w-full px-4 md:px-6 flex items-center">
        <div className="max-w-7xl mx-auto w-full">
          <div className="max-w-xl">
            <div className="flex items-center gap-2 mb-3">
              {show.status === 'updating' && (
                <span className="px-2.5 py-0.5 bg-accent-500 text-white text-xs font-medium rounded-full whitespace-nowrap">
                  更新中
                </span>
              )}
              <span className="text-foreground-500 text-sm">{show.year}</span>
              <span className="text-foreground-500 text-sm">·</span>
              <span className="text-foreground-500 text-sm">{show.category}</span>
              <span className="text-foreground-500 text-sm">·</span>
              <span className="text-primary-400 text-sm font-semibold">{show.rating}分</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 mb-3 leading-tight">
              {show.name}
            </h1>
            <p className="text-foreground-500 text-sm md:text-base leading-relaxed mb-5 line-clamp-3">
              {show.description}
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <Link
                to={`/detail/${show.id}`}
                className="px-6 py-2.5 bg-primary-500 text-background-50 font-semibold text-sm rounded-xl hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
              >
                <i className="ri-play-circle-fill text-lg"></i>
                立即观看
              </Link>
              <Link
                to={`/category/${show.categorySlug}`}
                className="px-5 py-2.5 bg-background-100/80 text-foreground-700 font-medium text-sm rounded-xl hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                更多{show.category}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-4 right-4 md:right-6 z-10 flex items-center gap-2">
        {featuredShows.map((_, index) => (
          <button
            key={index}
            onClick={() => goTo(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap ${
              index === current
                ? 'bg-primary-500 w-6'
                : 'bg-foreground-300 hover:bg-foreground-500'
            }`}
            aria-label={`切换到第${index + 1}个推荐`}
          />
        ))}
      </div>

      <button
        onClick={() => goTo((current - 1 + featuredShows.length) % featuredShows.length)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-100/60 text-foreground-600 hover:bg-background-200 hover:text-foreground-900 transition-all cursor-pointer whitespace-nowrap"
        aria-label="上一个"
      >
        <i className="ri-arrow-left-s-line text-lg"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-100/60 text-foreground-600 hover:bg-background-200 hover:text-foreground-900 transition-all cursor-pointer whitespace-nowrap"
        aria-label="下一个"
      >
        <i className="ri-arrow-right-s-line text-lg"></i>
      </button>
    </section>
  );
}